import React, { useState } from "react";
import moment from "moment";
import styles from "./Review.module.scss";
import fieldStyles from "../Form/Field.module.scss";
import { ReactComponent as BackIcon } from "../../assets/images/back.svg";
import Rating from "../Rating/Rating";
import { setAssistant } from "../../redux/assistants";
import api from "../../libs/api";
import Tracking from "@magic-engineering/magic-tracking";
import { toast } from "react-toastify";

const ratingOptions = ["BAD", "POOR", "FAIR", "GOOD", "INCREDIBLE"];
const ratingDescriptions = {
  "1 Star": "Bad: Didn’t meet my expectations at all.",
  "2 Star": "Poor: Didn’t meet most of my expectations.",
  "3 Star": "Fair: Met my expectations but there's room for improvement.",
  "4 Star": "Good: Assistant met my expectations and I’m satisfied.",
  "5 Star": "Incredible: Assistant exceeded my expectations!",
};

const errorMessages = {
  GREATER_THAN_7_DAYS:
    "Only feedbacks/reviews that have been submitted in the last 7 days can be updated.",
  default: "Something went wrong while saving review.",
};

const Review = ({ assistant, review, onDismiss, onUpdate }) => {
  const [rating, setRating] = useState(review ? review.rating : null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState(review ? review.rating_text : "");
  const [error, setError] = useState("");

  const onSubmit = async () => {
    setLoading(true);
    setError("");

    const rating_text = (comment || "").trim();

    try {
      if (review && review.feedback_id) {
        if (allowUpdate(review)) {
          Tracking.assistantReviewUpdated();
          const updatedReview = {
            ...review,
            rating,
            rating_text,
          };
          await updateAssistantFeedback(updatedReview);
          if (onUpdate) onUpdate(updatedReview);
          onDismiss();
        } else {
          toast.error(errorMessages.GREATER_THAN_7_DAYS);
        }
      } else {
        Tracking.assistantReviewSubmitted();
        const createdFeedback = await createAssistantFeedback({ assistant, rating, rating_text });

        // update assistant so review button will no longer show up in the dashboard
        setAssistant({
          ...assistant,
          review: {
            ...review,
            ...createdFeedback,
          },
        });
        onDismiss();
      }
    } catch (e) {
      console.error("failed to save assistant review", e);
      if (e.response && errorMessages[e.response.data]) {
        toast.error(errorMessages[e.response.data]);
        setError(errorMessages[e.response.data]);
      } else {
        setError(errorMessages.default);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`container-xl container-lg container-md container-fluid p-0 ${styles.Review}`}
      data-testid="Review"
    >
      <div
        data-testid="BackButton"
        className={styles.Back}
        onClick={() => {
          if (loading) return;
          onDismiss();
        }}
      >
        <BackIcon />
      </div>
      <h3 className="mt-4 mb-4">
        Review {assistant.name} for{" "}
        <span className="d-inline-block">
          {formatDate(assistant.review.start_date, assistant.review.end_date)}
        </span>
      </h3>
      <Rating options={ratingOptions} rating={rating} onChange={(val) => setRating(val)} />
      <input
        className={`${fieldStyles.field} form-control mt-4`}
        name={"comment"}
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        placeholder={"Optional: Add a comment"}
        type="text"
        data-testid="Comment"
        maxLength="300"
      />
      <div className={`form-control d-none ${error ? "is-invalid" : ""}`} />
      <div className="invalid-feedback" data-testid="ErrorMessage">
        {error}
      </div>
      <div className="d-flex">
        <button
          className={`${styles.Button} ${styles.ButtonBlue}`}
          disabled={loading || !rating}
          data-testid="SubmitButton"
          onClick={onSubmit}
        >
          {loading
            ? "Submitting..."
            : review && review.feedback_id
            ? "Update Review"
            : "Submit Review"}
        </button>
      </div>
      <div className={`${styles.Note}`}>
        Leaving an honest review can help your assistant improve! If you don’t leave a review we’ll
        assume your expectations were met, and assign the default 4/5 star rating for you
        convenience.
      </div>
      {Object.keys(ratingDescriptions).map((key) => (
        <span className={`${styles.NoteItem}`} key={key}>
          <span className={`${styles.NoteItemTitle}`}>{key}</span>
          <span className="ml-1 mr-1"> = </span>
          <span>{ratingDescriptions[key]}</span>
        </span>
      ))}
    </div>
  );
};

export const allowUpdate = (review) => {
  const now = moment();
  const billingWeekEnd = moment(review.end_date);
  const diff = now.diff(billingWeekEnd, "days");
  return diff <= 7;
};

export const formatDate = (start, end) => {
  if (moment(start).isSame(end, "month"))
    return `${moment(start).format("MMM D")}-${moment(end).format("D, YYYY")}`;
  return `${moment(start).format("MMM D")}-${moment(end).format("MMM D, YYYY")}`;
};

const createAssistantFeedback = async ({ assistant, rating, rating_text }) => {
  const { review, plan_id } = assistant;
  const fields = {
    plan_id,
    rating,
    rating_text,
    bill_id: review.bill_id,
  };

  const { data: feedback } = await api.post(`/assistants/feedbacks/`, fields);
  return feedback;
};

const updateAssistantFeedback = async ({ feedback_id, rating, rating_text }) => {
  await api.put(`/assistants/feedbacks/${feedback_id}`, { rating, rating_text });
};

export default Review;
